import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkle, CheckCircle, Terminal } from "@phosphor-icons/react";
import { ESTIMATOR } from "@/data/site";
import { submitLead, track as trackEvent, formatApiErrorDetail } from "@/lib/api";

const money = (n) => "$" + n.toLocaleString("en-CA");

export function ConciergeEstimator({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    track: "",
    projectType: "",
    projectLabel: "",
    base: [0, 0],
    scope: "",
    timeline: "",
    name: "",
    email: "",
    phone: "",
    company_website: "", // honeypot
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const reset = () => {
    setStep(0);
    setStatus("idle");
    setData({ track: "", projectType: "", projectLabel: "", base: [0, 0], scope: "", timeline: "", name: "", email: "", phone: "", company_website: "" });
  };

  const close = () => {
    onClose();
    setTimeout(reset, 300);
  };

  const estimate = useMemo(() => {
    if (!data.base[1]) return null;
    const scope = ESTIMATOR.scopes.find((s) => s.id === data.scope);
    const tl = ESTIMATOR.timelines.find((t) => t.id === data.timeline);
    const sMult = scope?.mult ?? 1;
    const tMult = tl?.mult ?? 1;
    const low = Math.round((data.base[0] * sMult * tMult) / 100) * 100;
    const high = Math.round((data.base[1] * sMult * tMult) / 100) * 100;
    return { low, high };
  }, [data]);

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setStatus("loading");
    setError("");
    try {
      await submitLead({
        name: data.name,
        email: data.email,
        phone: data.phone,
        track: data.track,
        project_type: data.projectLabel,
        scope: data.scope,
        timeline: data.timeline,
        estimate_low: estimate?.low,
        estimate_high: estimate?.high,
        source: "ai_concierge",
        company_website: data.company_website,
      });
      trackEvent("concierge_complete", { track: data.track });
      setStatus("done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setStatus("error");
    }
  };

  const types = data.track ? ESTIMATOR.projectTypes[data.track] : [];

  const Option = ({ active, onClick, children, testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left border px-4 py-3 font-mono-tech text-sm transition-colors ${
        active ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/60 text-foreground"
      }`}
    >
      {children}
    </button>
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-background/80 backdrop-blur-sm p-0 sm:p-6"
          data-testid="concierge-overlay"
          onClick={close}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-xl bg-card border border-border max-h-[92vh] overflow-y-auto"
            data-testid="concierge-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <Terminal size={18} className="text-primary" />
                <span className="font-mono-tech text-xs tracking-widest uppercase">
                  MRE Project Concierge
                </span>
              </div>
              <button onClick={close} aria-label="Close" data-testid="concierge-close" className="text-muted-foreground hover:text-primary">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 blueprint-grid-fine">
              {/* Progress */}
              {status !== "done" && (
                <div className="flex gap-1 mb-6">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <span key={i} className={`h-1 flex-1 ${i <= step ? "bg-primary" : "bg-border"}`} />
                  ))}
                </div>
              )}

              {status === "done" ? (
                <div className="text-center py-6" data-testid="concierge-done">
                  <CheckCircle size={44} weight="light" className="text-primary mx-auto mb-4" />
                  <h3 className="font-display uppercase tracking-tight text-2xl">You're all set</h3>
                  {estimate && (
                    <p className="mt-3 font-mono-tech text-primary text-lg">
                      {money(estimate.low)} – {money(estimate.high)}
                    </p>
                  )}
                  <p className="mt-3 text-muted-foreground">
                    That's a ballpark — not a quote. We'll follow up within one business day with a
                    free consultation and a transparent price.
                  </p>
                  <button onClick={close} className="mt-6 bg-primary text-primary-foreground font-mono-tech text-xs tracking-widest uppercase px-6 h-11">
                    Done
                  </button>
                </div>
              ) : (
                <>
                  {/* Step 0: track */}
                  {step === 0 && (
                    <div data-testid="concierge-step-track">
                      <p className="font-mono-tech text-xs text-muted-foreground mb-1">/ Step 01</p>
                      <h3 className="font-display uppercase tracking-tight text-2xl mb-5">What do you need?</h3>
                      <div className="space-y-3">
                        {ESTIMATOR.tracks.map((t) => (
                          <Option
                            key={t.id}
                            active={data.track === t.id}
                            testId={`concierge-track-${t.id}`}
                            onClick={() => {
                              setData((d) => ({ ...d, track: t.id, projectType: "", projectLabel: "", base: [0, 0] }));
                              next();
                            }}
                          >
                            {t.label}
                          </Option>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 1: project type */}
                  {step === 1 && (
                    <div data-testid="concierge-step-type">
                      <p className="font-mono-tech text-xs text-muted-foreground mb-1">/ Step 02</p>
                      <h3 className="font-display uppercase tracking-tight text-2xl mb-5">Project type</h3>
                      <div className="space-y-3">
                        {types.map((t) => (
                          <Option
                            key={t.id}
                            active={data.projectType === t.id}
                            testId={`concierge-type-${t.id}`}
                            onClick={() => {
                              setData((d) => ({ ...d, projectType: t.id, projectLabel: t.label, base: t.base }));
                              next();
                            }}
                          >
                            {t.label}
                          </Option>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 2: scope */}
                  {step === 2 && (
                    <div data-testid="concierge-step-scope">
                      <p className="font-mono-tech text-xs text-muted-foreground mb-1">/ Step 03</p>
                      <h3 className="font-display uppercase tracking-tight text-2xl mb-5">How ambitious?</h3>
                      <div className="space-y-3">
                        {ESTIMATOR.scopes.map((s) => (
                          <Option
                            key={s.id}
                            active={data.scope === s.id}
                            testId={`concierge-scope-${s.id}`}
                            onClick={() => {
                              setData((d) => ({ ...d, scope: s.id }));
                              next();
                            }}
                          >
                            {s.label}
                          </Option>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 3: timeline */}
                  {step === 3 && (
                    <div data-testid="concierge-step-timeline">
                      <p className="font-mono-tech text-xs text-muted-foreground mb-1">/ Step 04</p>
                      <h3 className="font-display uppercase tracking-tight text-2xl mb-5">Timeline?</h3>
                      <div className="space-y-3">
                        {ESTIMATOR.timelines.map((t) => (
                          <Option
                            key={t.id}
                            active={data.timeline === t.id}
                            testId={`concierge-timeline-${t.id}`}
                            onClick={() => {
                              setData((d) => ({ ...d, timeline: t.id }));
                              next();
                            }}
                          >
                            {t.label}
                          </Option>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 4: contact + estimate */}
                  {step === 4 && (
                    <div data-testid="concierge-step-contact">
                      <p className="font-mono-tech text-xs text-muted-foreground mb-1">/ Step 05</p>
                      <h3 className="font-display uppercase tracking-tight text-2xl mb-2">Your ballpark</h3>
                      {estimate && (
                        <div className="border border-primary/40 bg-primary/5 px-4 py-4 mb-5">
                          <p className="font-mono-tech text-[11px] tracking-widest uppercase text-muted-foreground flex items-center gap-2">
                            <Sparkle size={13} className="text-primary" /> Estimated range
                          </p>
                          <p className="font-display text-3xl mt-1 text-primary" data-testid="concierge-estimate">
                            {money(estimate.low)} – {money(estimate.high)}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Ballpark only — your free quote is detailed and transparent.
                          </p>
                        </div>
                      )}
                      <div className="space-y-3">
                        <input
                          placeholder="Name *"
                          value={data.name}
                          onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))}
                          className="w-full bg-secondary/60 border border-border focus:border-primary outline-none px-4 h-12 font-mono-tech text-sm"
                          data-testid="concierge-name"
                        />
                        <input
                          placeholder="Email *"
                          type="email"
                          value={data.email}
                          onChange={(e) => setData((d) => ({ ...d, email: e.target.value }))}
                          className="w-full bg-secondary/60 border border-border focus:border-primary outline-none px-4 h-12 font-mono-tech text-sm"
                          data-testid="concierge-email"
                        />
                        <input
                          placeholder="Phone (optional)"
                          value={data.phone}
                          onChange={(e) => setData((d) => ({ ...d, phone: e.target.value }))}
                          className="w-full bg-secondary/60 border border-border focus:border-primary outline-none px-4 h-12 font-mono-tech text-sm"
                          data-testid="concierge-phone"
                        />
                        <div aria-hidden="true" className="hp-trap" tabIndex={-1}>
                          <input
                            type="text"
                            name="company_website"
                            tabIndex={-1}
                            autoComplete="off"
                            value={data.company_website}
                            onChange={(e) => setData((d) => ({ ...d, company_website: e.target.value }))}
                            data-testid="concierge-hp-company"
                          />
                        </div>
                      </div>
                      {status === "error" && (
                        <p className="text-sm text-destructive font-mono-tech mt-3">{error}</p>
                      )}
                      <button
                        onClick={submit}
                        disabled={!data.name || !data.email || status === "loading"}
                        data-testid="concierge-submit"
                        className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono-tech text-xs tracking-widest uppercase h-12 hover:bg-[hsl(16,100%,55%)] transition-colors disabled:opacity-50"
                      >
                        {status === "loading" ? "Sending…" : "Send & book free consult"}
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  )}

                  {step > 0 && (
                    <button
                      onClick={back}
                      data-testid="concierge-back"
                      className="mt-6 inline-flex items-center gap-2 font-mono-tech text-xs tracking-widest uppercase text-muted-foreground hover:text-primary"
                    >
                      <ArrowLeft size={14} /> Back
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
